import React from 'react';
import { DragDropContext, Droppable, DropResult } from '@hello-pangea/dnd';
import { Box, Paper, Typography, useTheme, Chip } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import StoreIcon from '@mui/icons-material/Store';
import { Herramienta, Obra } from '../../types';
import HerramientaCard from './HerramientaCard';

interface HerramientaKanbanProps {
    herramientas: Herramienta[];
    obras: Obra[];
    onToolMove: (toolId: string, targetObraId: string | null) => Promise<void>;
    onEdit?: (herramienta: Herramienta) => void;
    onDelete?: (id: string) => void;
}

const HerramientaKanban: React.FC<HerramientaKanbanProps> = ({ herramientas, obras, onToolMove, onEdit, onDelete }) => {
    const theme = useTheme();

    // Group tools by obraId. 
    // "Oficina" is removed. Default is "ByB" project.
    // Tools with no obraId (legacy office) should be displayed in "ByB" column if possible.
    const columns = React.useMemo(() => {
        const cols: Record<string, Herramienta[]> = {};

        // Find ByB project to use as default
        const obraByB = obras.find(o => o.nombre.toUpperCase() === 'BYB' || o.nombre.toUpperCase() === 'B&B');
        const bybId = obraByB?.id;

        // Initialize entries for all obras (not just active, as per user feedback "no me aparecen todas las obras")
        obras.forEach(obra => {
            // We can filter out 'completada' if desired, but user said "no me aparecen todas", implying they want to see them.
            // Let's verify if we should exclude 'deleted' or similar if that state exists.
            // Based on types, estados are: 'activa' | 'completada' | 'suspendida'
            // Let's show all for now.
            cols[obra.id] = [];
        });

        // Ensure ByB column exists even if not in list (though it likely is)
        if (bybId && !cols[bybId]) {
            cols[bybId] = [];
        }

        herramientas.forEach(tool => {
            if (tool.obraId && cols[tool.obraId]) {
                cols[tool.obraId].push(tool);
            } else if (tool.obraId) {
                // Tool in an obra not in our initialized list (maybe suspended/completed?)
                if (!cols[tool.obraId]) cols[tool.obraId] = [];
                cols[tool.obraId].push(tool);
            } else {
                // No obraId (Old "Oficina" status). Put in ByB if exists.
                if (bybId && cols[bybId]) {
                    cols[bybId].push(tool);
                } else {
                    // Fallback if ByB not found: create a "Sin Asignar" column
                    if (!cols['metrics_unassigned']) cols['metrics_unassigned'] = [];
                    cols['metrics_unassigned'].push(tool);
                }
            }
        });

        return cols;
    }, [herramientas, obras]);

    // Ordered list: ByB first, then others alphabetically
    const columnOrder = React.useMemo(() => {
        const obraIds = Object.keys(columns);

        // Find ByB id
        const obraByB = obras.find(o => o.nombre.toUpperCase() === 'BYB' || o.nombre.toUpperCase() === 'B&B');
        const bybId = obraByB?.id;

        const otherObraIds = obraIds
            .filter(id => id !== bybId && id !== 'metrics_unassigned')
            .sort((a, b) => {
                const nameA = obras.find(o => o.id === a)?.nombre || '';
                const nameB = obras.find(o => o.id === b)?.nombre || '';
                return nameA.localeCompare(nameB);
            });

        const finalOrder = [];

        // ByB First
        if (bybId && columns[bybId]) finalOrder.push(bybId);

        // Then Unassigned if any
        if (columns['metrics_unassigned']) finalOrder.push('metrics_unassigned');

        // Then others
        finalOrder.push(...otherObraIds);

        return finalOrder;
    }, [columns, obras]);

    const getColumnName = (colId: string) => {
        if (colId === 'metrics_unassigned') return 'Sin Asignar';
        const obra = obras.find(o => o.id === colId);
        return obra ? obra.nombre : 'Obra Desconocida';
    };

    const handleDragEnd = (result: DropResult) => {
        const { destination, source, draggableId } = result;

        if (!destination) return;
        if (destination.droppableId === source.droppableId && destination.index === source.index) return;

        // If dropped in 'metrics_unassigned' treat as null (shouldnt happen mostly)
        // If dropped in ByB or any other project, pass the ID.
        // HerramientasView handles logic. If targetID is ByB ID, it saves ByB ID.

        let targetId: string | null = destination.droppableId;
        if (targetId === 'metrics_unassigned') targetId = null; // Mapping back to "Oficina" logic just in case

        onToolMove(draggableId, targetId);
    };

    const isByB = (colId: string) => {
        const obra = obras.find(o => o.id === colId);
        return obra && (obra.nombre.toUpperCase() === 'BYB' || obra.nombre.toUpperCase() === 'B&B');
    };

    return (
        <DragDropContext onDragEnd={handleDragEnd}>
            <Box
                sx={{
                    display: 'flex',
                    overflowX: 'auto',
                    pb: 2,
                    gap: 2,
                    height: '100%',
                    minHeight: 0,
                    scrollSnapType: 'x mandatory',
                    scrollBehavior: 'smooth',
                    px: { xs: 2, sm: 0 }, // Add padding on mobile to center content better
                    '&::-webkit-scrollbar': {
                        height: 8,
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0,0,0,0.1)',
                        borderRadius: 4
                    }
                }}
            >
                {columnOrder.map(colId => {
                    const tools = columns[colId];
                    const isDefaultCol = isByB(colId) || colId === 'metrics_unassigned';
                    const colName = getColumnName(colId);

                    return (
                        <Paper
                            key={colId}
                            elevation={0}
                            sx={{
                                minWidth: { xs: '85vw', sm: 300, md: 320 },
                                width: { xs: '85vw', sm: 300, md: 320 },
                                scrollSnapAlign: 'center',
                                display: 'flex',
                                flexDirection: 'column',
                                bgcolor: isDefaultCol ? theme.palette.grey[100] : theme.palette.grey[50], // Highlight ByB slightly
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                height: '100%'
                            }}

                        >
                            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: isDefaultCol ? '#e3f2fd' : 'transparent' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    {isDefaultCol ? <StoreIcon color="primary" /> : <BusinessIcon color="action" />}
                                    <Typography variant="subtitle1" fontWeight="bold" noWrap title={colName}>
                                        {colName}
                                    </Typography>
                                </Box>
                                <Chip
                                    label={`${tools.length} Herramientas`}
                                    size="small"
                                    color={isDefaultCol ? "primary" : "default"}
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                            </Box>

                            <Droppable droppableId={colId}>
                                {(provided, snapshot) => (
                                    <Box
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                        sx={{
                                            p: 1.5,
                                            flexGrow: 1,
                                            overflowY: 'auto',
                                            bgcolor: snapshot.isDraggingOver ? 'action.hover' : 'transparent',
                                            transition: 'background-color 0.2s'
                                        }}
                                    >
                                        {tools.map((tool, index) => (
                                            <HerramientaCard key={tool.id} herramienta={tool} index={index} onEdit={onEdit} onDelete={onDelete} />
                                        ))}
                                        {provided.placeholder}
                                    </Box>
                                )}
                            </Droppable>
                        </Paper>
                    );
                })}
            </Box>
        </DragDropContext >
    );
};

export default HerramientaKanban;
